/**
 * Client-side utilities for Web Push Notifications
 * Handles subscription, unsubscription, and permission management
 */

// Check if push notifications are supported
export function isPushSupported(): boolean {
  if (typeof window === "undefined") return false
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
}

// Get current notification permission status
export function getNotificationPermission(): NotificationPermission {
  if (!isPushSupported()) return "denied"
  return Notification.permission
}

// Request notification permission from user
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!isPushSupported()) {
    throw new Error("Push notifications are not supported in this browser")
  }

  const permission = await Notification.requestPermission()
  console.log("[Push] Permission status:", permission)
  return permission
}

// Subscribe to push notifications
export async function subscribeToPushNotifications(): Promise<PushSubscription | null> {
  try {
    // Check support
    if (!isPushSupported()) {
      throw new Error("Push notifications are not supported")
    }

    // Check permission
    if (Notification.permission !== "granted") {
      const permission = await requestNotificationPermission()
      if (permission !== "granted") {
        throw new Error("Notification permission denied")
      }
    }

    // Get service worker registration
    const registration = await navigator.serviceWorker.ready
    console.log("[Push] Service worker ready")

    // Check for existing subscription
    let subscription = await registration.pushManager.getSubscription()

    if (subscription) {
      console.log("[Push] Already subscribed:", subscription.endpoint)
      // Still send to server to ensure it's saved
      await sendSubscriptionToServer(subscription)
      return subscription
    }

    // Get VAPID public key from environment
    const vapidPublicKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY
    if (!vapidPublicKey) {
      throw new Error("VAPID public key not configured")
    }

    // Convert VAPID key from base64 to Uint8Array
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey)

    // Subscribe to push notifications
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey as BufferSource,
    })

    console.log("[Push] New subscription created:", subscription.endpoint)

    // Send subscription to server
    await sendSubscriptionToServer(subscription)

    return subscription
  } catch (error) {
    console.error("[Push] Failed to subscribe:", error)
    throw error
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPushNotifications(): Promise<boolean> {
  try {
    if (!isPushSupported()) {
      throw new Error("Push notifications are not supported")
    }

    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()

    if (!subscription) {
      console.log("[Push] No active subscription to unsubscribe from")
      return false
    }

    // Unsubscribe from push service
    const success = await subscription.unsubscribe()
    console.log("[Push] Unsubscribed from push service:", success)

    // Remove subscription from server
    if (success) {
      await removeSubscriptionFromServer(subscription.endpoint)
    }

    return success
  } catch (error) {
    console.error("[Push] Failed to unsubscribe:", error)
    throw error
  }
}

// Get current push subscription
export async function getPushSubscription(): Promise<PushSubscription | null> {
  try {
    if (!isPushSupported()) return null

    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    return subscription
  } catch (error) {
    console.error("[Push] Failed to get subscription:", error)
    return null
  }
}

// Check if user is currently subscribed
export async function isSubscribed(): Promise<boolean> {
  const subscription = await getPushSubscription()
  return subscription !== null
}

// Send subscription to server
async function sendSubscriptionToServer(subscription: PushSubscription): Promise<void> {
  const response = await fetch("/api/push/subscribe", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(subscription.toJSON()),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || "Failed to save subscription")
  }

  console.log("[Push] Subscription saved to server")
}

// Remove subscription from server
async function removeSubscriptionFromServer(endpoint: string): Promise<void> {
  const response = await fetch("/api/push/unsubscribe", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ endpoint }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || "Failed to remove subscription")
  }

  console.log("[Push] Subscription removed from server")
}

// Helper function to convert base64 VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }

  return outputArray
}

// Check if running as installed PWA (enhanced with multiple detection methods)
export function isInstalledPWA(): boolean {
  if (typeof window === "undefined") return false

  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent)

  // Method 1: Check display-mode media query (most reliable for modern browsers)
  const isStandaloneDisplayMode = window.matchMedia("(display-mode: standalone)").matches

  // Method 2: Check iOS-specific standalone property
  const isIOSStandalone = isIOS && (navigator as any).standalone === true

  // Method 3: Check for minimal-ui or fullscreen modes (also indicate PWA)
  const isMinimalUI = window.matchMedia("(display-mode: minimal-ui)").matches
  const isFullscreen = window.matchMedia("(display-mode: fullscreen)").matches

  // Method 4: Check if launched from home screen (referrer check)
  const launchedFromHomeScreen =
    typeof document !== "undefined" && document.referrer === "" && !window.location.search.includes("utm_source")

  const isPWA = isStandaloneDisplayMode || isIOSStandalone || isMinimalUI || isFullscreen

  // Log for analytics (helpful for debugging)
  if (isPWA && typeof console !== "undefined") {
    console.log("[PWA] Detected as installed PWA", {
      standalone: isStandaloneDisplayMode,
      iOSStandalone: isIOSStandalone,
      minimalUI: isMinimalUI,
      fullscreen: isFullscreen,
      isIOS,
    })
  }

  return isPWA
}

// Check if device is iOS
export function isIOSDevice(): boolean {
  if (typeof window === "undefined") return false
  return /iPhone|iPad|iPod/.test(navigator.userAgent)
}

// Check if browser supports push (not just notifications)
export function canUsePushNotifications(): boolean {
  // iOS Safari only supports push in installed PWA
  if (isIOSDevice()) {
    return isInstalledPWA() && isPushSupported()
  }

  // Other browsers support push notifications
  return isPushSupported()
}
